import { api } from "@/lib/api-client"

// =============================================================================
// What's New management
// =============================================================================

export type AdminWhatsNewVersion = {
  version: string
  locales: string[]
}

export type AdminWhatsNewEffectiveSource = "pinned" | "manifest" | "auto" | "none"

export type AdminWhatsNewVersionsResponse = {
  versions: AdminWhatsNewVersion[]
  app_version: string | null
  /** Active version from the override manifest, or null when the manifest
   * is missing or parked as a skeleton (`{"version": null}`). */
  override_version: string | null
  /** True when the manifest blob exists, regardless of whether it has an
   * active version. Distinguishes "parked skeleton" from "missing entirely". */
  override_present: boolean
  /** True when `override_version` is set but no matching blob exists. The
   * public resolver falls through to the next layer in this case; the UI
   * should flag the tile inline so it's clear the override isn't actually
   * driving anything. */
  override_blob_missing: boolean
  /** Filename actually used (canonical or legacy), or null if absent. */
  override_filename: string | null
  /** Canonical filename the dashboard should advertise. */
  override_filename_canonical: string
  pinned_version: string | null
  /** True when `pinned_version` is set but no matching blob exists. Like
   * `override_blob_missing`, the public resolver falls through. */
  pinned_blob_missing: boolean
  /** Highest PEP 440 version ≤ app_version. Excludes notice-style files. */
  applicable_version: string | null
  effective_version: string | null
  effective_source: AdminWhatsNewEffectiveSource
  warnings: string[]
}

export async function getAdminWhatsNewVersions(
  options: { force?: boolean } = {}
): Promise<AdminWhatsNewVersionsResponse> {
  // `force=true` bypasses the 60s server-side _list_versioned_blobs cache so
  // the dashboard's Refresh button reflects newly-uploaded blobs immediately.
  // Override + Cosmos pin are always read fresh — this flag only affects the
  // version-table enumeration cost.
  const path = options.force ? "/admin/whats-new/versions?force=true" : "/admin/whats-new/versions"
  const response = await api.get(path)
  return response.data || response
}

export async function setAdminWhatsNewPinned(
  version: string | null
): Promise<{ success: boolean; pinned_version: string | null }> {
  const response = await api.put("/admin/whats-new/pinned", { version })
  return response.data || response
}
